import React from "react";
import { User, Monitor, Brain, Zap, Activity } from "lucide-react";

/**
 * Reusable component for selecting Game Mode and Difficulty.
 * @param {Object} props
 * @param {'PvP' | 'PvC'} props.gameMode - Current game mode
 * @param {Function} props.setGameMode - Function to update game mode
 * @param {'Easy' | 'Medium' | 'Hard'} props.difficulty - Current difficulty
 * @param {Function} props.setDifficulty - Function to update difficulty
 * @param {Function} props.onStart - Function to start the game
 */
const GameOptions = ({
  gameMode,
  setGameMode,
  difficulty,
  setDifficulty,
  onStart,
}) => {
  return (
    <div className="bg-white rounded-lg p-6 max-w-lg mx-auto mb-8 animate-fade-in">
      <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center justify-center">
        <Activity className="w-6 h-6 mr-2 text-blue-500" />
        Game Settings
      </h3>

      {/* Game Mode Selection */}
      <div className="mb-6">
        <label className="block text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wider">
          Game Mode
        </label>
        <div className="grid grid-cols-2 gap-4">
          <button
            onClick={() => setGameMode("PvP")}
            className={`
              flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all duration-200
              ${
                gameMode === "PvP"
                  ? "border-blue-500 bg-blue-50 text-blue-700 shadow-md transform scale-105"
                  : "border-gray-200 hover:border-blue-300 hover:bg-gray-50 text-gray-600"
              }
            `}
          >
            <div className="flex space-x-2 mb-2">
              <User className="w-6 h-6" />
              <User className="w-6 h-6" />
            </div>
            <span className="font-medium">2 Players</span>
          </button>

          <button
            onClick={() => setGameMode("PvC")}
            className={`
              flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all duration-200
              ${
                gameMode === "PvC"
                  ? "border-purple-500 bg-purple-50 text-purple-700 shadow-md transform scale-105"
                  : "border-gray-200 hover:border-purple-300 hover:bg-gray-50 text-gray-600"
              }
            `}
          >
            <div className="flex space-x-2 mb-2">
              <User className="w-6 h-6" />
              <Monitor className="w-6 h-6" />
            </div>
            <span className="font-medium">Vs Computer</span>
          </button>
        </div>
      </div>

      {/* Difficulty Selection (Only visible for PvC) */}
      {gameMode === "PvC" && (
        <div className="mb-8 animate-slide-down">
          <label className="block text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wider">
            Difficulty
          </label>
          <div className="grid grid-cols-3 gap-3">
            {[
              { id: "Easy", icon: Zap, color: "green" },
              { id: "Medium", icon: Activity, color: "yellow" },
              { id: "Hard", icon: Brain, color: "red" },
            ].map(({ id, icon: Icon, color }) => (
              <button
                key={id}
                onClick={() => setDifficulty(id)}
                className={`
                  flex flex-col items-center p-3 rounded-lg border-2 transition-all duration-200
                  ${
                    difficulty === id
                      ? `border-${color}-500 bg-${color}-50 text-${color}-700 shadow-sm font-bold`
                      : "border-gray-200 hover:bg-gray-50 text-gray-500"
                  }
                `}
              >
                <Icon
                  className={`w-5 h-5 mb-1 ${
                    difficulty === id ? `text-${color}-500` : "text-gray-400"
                  }`}
                />
                <span className="text-sm">{id}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <button
        onClick={onStart}
        className="w-full py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-lg font-bold rounded-xl shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all duration-200 flex items-center justify-center"
      >
        Start Game
      </button>
    </div>
  );
};

export default GameOptions;
